import { DateWrapper } from '@trucktrax/trucktrax-common';
import React from 'react';

import C from '../../../constants/messagingConstants';
import { getFullName } from '../../../util/adminUtil';
import { ConversationMessagesDto, Message, PartyMember } from './index.d';
import ThumbsUpSvg from './MessageCenter/RightSidebar/ThumbsUpSvg';
import { DTO_URL_PREFIXES } from '../../../constants/appConstants';

export interface LatestMessageDto {
  latestMessage?: Message | '';
  from: {
    firstName: string;
    lastName: string;
    url: string;
  };
}

export interface TrucksToDriverMap {
  url: string;
  truckDto: {
    truckAlias: string;
  };
}

export interface RecipientNameDto extends Message {
  latestMessage?: Message | '';
  partiesList: PartyMember[];
}

// Type for truck alias lookup function
export type TruckAliasLookupFn = (driverUrl?: string) => string | undefined;

// Type for separated author and truck info
export interface AuthorWithTruckInfo {
  authorName: string;
  truckAlias?: string;
}

// Enhanced function to format author name with truck prefix (legacy format)
export function formatAuthorWithTruck(
  authorName: string,
  driverUrl?: string,
  getTruckAlias?: TruckAliasLookupFn,
): string {
  if (!getTruckAlias || !driverUrl) {
    return authorName;
  }

  const truckAlias = getTruckAlias(driverUrl);

  return truckAlias ? `[${truckAlias}] ${authorName}` : authorName;
}

// Function to get author and truck info separately for better display control
export function getAuthorWithTruckInfo(
  authorName: string,
  driverUrl?: string,
  getTruckAlias?: TruckAliasLookupFn,
): AuthorWithTruckInfo {
  if (!getTruckAlias || !driverUrl) {
    return { authorName };
  }

  const truckAlias = getTruckAlias(driverUrl);
  return {
    authorName,
    truckAlias: truckAlias || undefined,
  };
}

// return 'from' field of the message
export function getAuthor(
  message?: Message,
  partiesList?: PartyMember[] | [],
  direction?: 'to' | 'from',
  getTruckAlias?: TruckAliasLookupFn,
) {
  // TODO: modify to use region url after gtx-1542
  // List of party members EXCLUDING dispatch
  const partiesWithoutMe =
    partiesList &&
    partiesList.filter((party: PartyMember) => !party.url.includes(DTO_URL_PREFIXES.REGIONS));

  // First check, If there is a list of parties, We only want last names
  if (partiesWithoutMe && partiesWithoutMe.length > 1) {
    const commaSeparateParties = partiesWithoutMe
      .filter((p: PartyMember) => p.lastName)
      .map((p: PartyMember) => {
        const baseName = p.lastName;
        return formatAuthorWithTruck(baseName, p.url, getTruckAlias);
      })
      .join(', ');
    return commaSeparateParties;
    // Second check, if the list is Exactly 1, then we want LastName, FirstName
  }
  if (partiesWithoutMe && partiesWithoutMe.length === 1) {
    const party = partiesWithoutMe[0];
    const baseName = getFullName(party) || `${party.lastName}, ${party.firstName}`;
    return formatAuthorWithTruck(baseName, party.url, getTruckAlias);
    // Last Check, if the message 'direction' exists (To or From)
  }
  if (
    message &&
    direction &&
    message[direction] &&
    message[direction].firstName &&
    message[direction].lastName
  ) {
    const baseName =
      getFullName(message[direction]) ||
      `${message[direction].lastName}, ${message[direction].firstName}`;
    return formatAuthorWithTruck(baseName, message[direction].url, getTruckAlias);
  }
  return '';
}

export function getRecipientNames(
  message: ConversationMessagesDto,
  getTruckAlias?: TruckAliasLookupFn,
) {
  if (message && message.latestMessage && message.partiesList) {
    return getAuthor(message.latestMessage, message.partiesList, 'from', getTruckAlias);
  }
  if (message && !message.latestMessage) {
    return getAuthor(message.message, message.partiesList, 'from', getTruckAlias);
  }
  return '';
}

export function getRecipientNamesWithTruckInfo(
  message: ConversationMessagesDto,
  getTruckAlias?: TruckAliasLookupFn,
): AuthorWithTruckInfo[] {
  const recipients: AuthorWithTruckInfo[] = [];

  if (!message?.partiesList) {
    return recipients;
  }

  message.partiesList.forEach(party => {
    if (party.fullName && party.url.includes(DTO_URL_PREFIXES.DRIVERS)) {
      const truckInfo = getAuthorWithTruckInfo(party.fullName, party.url, getTruckAlias);
      recipients.push(truckInfo);
    } else if (party.fullName) {
      recipients.push({ authorName: party.fullName });
    }
  });

  return recipients;
}

export function getAuthorWithDriverId(
  message?: LatestMessageDto,
  getTruckAlias?: TruckAliasLookupFn,
) {
  const latestMessage = message && message.latestMessage;
  if (latestMessage) {
    const baseName =
      getFullName(latestMessage.from) ||
      `${latestMessage.from.lastName}, ${latestMessage.from.firstName}`;
    return formatAuthorWithTruck(baseName, latestMessage.from.url, getTruckAlias);
  }
  if (message && message.from) {
    const baseName =
      getFullName(message.from) ||
      (message.from.firstName
        ? `${message.from.lastName}, ${message.from.firstName}`
        : `${message.from.lastName}`);
    return formatAuthorWithTruck(baseName, message.from.url, getTruckAlias);
  }
  return '';
}

export function formatTime(time: Date) {
  if (time && DateWrapper.isValidDate(time)) {
    const today = DateWrapper.now.startOfDay;
    const yesterday = today.previousDay;
    const now = new DateWrapper(time);
    const timeString = now.toString('h:mma');

    if (now.isAfterOrEqual(today)) {
      // today show time
      return timeString;
    }
    if (now.isBetween(yesterday, today) || now.isEqual(yesterday)) {
      // yesterday show string
      return `Yesterday, ${timeString}`;
    }
    if (now.isBefore(yesterday)) {
      // before yesterday show string
      const dateString = now.toString('M/d/yy');
      return `${dateString}, ${timeString}`;
    }
  }
  // unknown format, show nothing
  return '';
}

export function checkAckTime(sentDateString?: string, acknowledgedDateString?: string) {
  const sent = new DateWrapper(sentDateString);
  const acknowledged = new DateWrapper(acknowledgedDateString);
  const defaultAckTimeInTheFuture = new DateWrapper('9999-12-31T00:00:00.000Z');
  if (sentDateString && acknowledgedDateString) {
    return acknowledged.isEqual(defaultAckTimeInTheFuture) || sent.isAfter(acknowledged);
  }
  return true;
}

export function getTruckId(trucksToDriversMap: TrucksToDriverMap, message: Message) {
  const map = new Map(Object.entries(trucksToDriversMap));
  let truckId = '';
  if (trucksToDriversMap && message) {
    map.forEach((value: TrucksToDriverMap) => {
      if (message.from && value.url === message.from.url) {
        truckId = value.truckDto.truckAlias;
        return false;
      }
      return true;
    });
  }
  return truckId;
}

export function checkBodyForIconString(body: string, dataTest?: string | null) {
  return body === C.THUMB_UP_ICON_STRING ? <ThumbsUpSvg props={dataTest} /> : body;
}
